export const SHORT = {
  process: "proc",
  recommend: "recom",
  compare: "comp",
  comparison: "compsn",
  first: "fst",
  second: "scd",
} as const;

export const SHORT_FUNERAL = {
  parlor: "prlr",
  crematorium: "crema",
  funeral: "fune",
  facility: "fac",
  facilities: "facs",
} as const;

type Short = typeof SHORT;
type ShortFuneral = typeof SHORT_FUNERAL;

export type CommonEventProps = ActionEvent | ClickEventType;

// GTM dataLayer push와 Hackle track에 함께 실리는 부가 속성.
// 전부 optional이라 기존 호출부(160곳/99파일)는 그대로 동작한다.
export type EventProperties = Record<string, string | number | boolean>;

export interface ClickEventType {
  action?: "click";
  domain: keyof typeof EventDomainType;
  path: EventPathType;
  trigger_and_target?: EventTriggerAndTargetType;
  text?: string;
  properties?: EventProperties;
}

interface ActionEvent {
  action:
    | "call"
    | "goi_call"
    | "move_to_goi"
    | "gyeongsang_open_parlor50_modal"
    // A/B 변이 확정 시 1회 발화. properties.experiment_variation = "{key}-{변이}".
    // Hackle은 노출을 자동 기록하지만 그건 도메인별 $id라 goifuneral 리드와 조인 불가.
    // 이 이벤트가 GA4/BQ에서 variation별 리드를 쪼갤 수 있는 유일한 다리다.
    | "experiment_exposure"
    // 빠르게 알아보기 카드로 게스트 견적이 생성됐을 때(201) 1회. properties.portal로 포털 구분.
    // 개별 이벤트(click-{domain}-{path}-qk-done)와 중복 발화하지만 목적이 다르다.
    // 개별 = 포털별 이름이라 Hackle 지표에 6개를 나열해야 하고, 이건 1개로 6개 포털이 합산된다.
    // ActionEvent라 domain이 이름에 안 들어가는 게 핵심(체크리스트 이벤트와 같은 구조).
    | "qk_created";
  properties?: EventProperties;
}

export enum EventDomainType {
  "ch" = "ch",
  "dggb" = "dggb",
  "gangwon" = "gangwon",
  "gyeongsang" = "gyeongsang",
  "honam" = "honam",
  "metro" = "metro",
  "common" = "common",
  "heysangjo" = "heysangjo",
}

export type EventPathType =
  | "header"
  | "footer"
  | "home"
  | "benef"
  | "snackbar"
  | "support"
  | "all_prcss_recm"
  | "nat"
  | "nat_hr"
  | "nat_hr_fac"
  | "nat_hr_crema"
  | "nat_merit"
  | "nat_merit_crema"
  | "nat_merit_facility"
  | "nat_hr_fr_crema"
  | "nat_hr_fr_facility"
  | "nat_fr-crema"
  | "nat_fr_facility"
  | "fr_crema"
  | "fr_benef"
  | "prlrs"
  | "prlr"
  | "prlr_recm"
  | "prlr_binso"
  | "prlr_flower"
  | "prlr_price"
  | "prlr_intro"
  | "prlr_service"
  | "prlr_list"
  | "prlr_list_sigun"
  | "prlr_item"
  | "prlr_item_intro"
  | "prlr_item_binso"
  | "prlr_item_flower"
  | "prlr_item_price"
  | "prlr_item_service"
  | "prlr_compare"
  | "facil_item_intro"
  | "facil_item_price"
  | "facil_item_reservation"
  | "price"
  | "benef_crema"
  | "benef_sup"
  | "benef_sup_fr_crema"
  | "benef_sup_fr_facility"
  | "benef_pub_facility"
  | "benef_facility"
  | "facility"
  | "facility_price"
  | "benef_sup_fr_fac"
  | "chk_compare"
  | "all_prcss_recm"
  | "all-funeral"
  | "veterans"
  | "veterans_facility"
  | "veterans_crema"
  | "compare_prlr"
  | "fr-crema"
  | "fr_facility"
  | "funeral_recm"
  | "fr_nat"
  | "funeral_binso"
  | "funeral_price"
  | "funeral_place"
  | "funeral_service"
  | "funeral_intro"
  | "funeral_flower"
  | "funeral_place_binso"
  | "funeral_place_flower"
  | "funeral_place_price"
  | "funeral_place_service"
  | "funeral_place_estimate"
  | `${ShortFuneral["funeral"]}_${Short["process"]}_${Short["recommend"]}`
  | `${ShortFuneral["funeral"]}_${Short["process"]}_${Short["recommend"]}_list`
  | `${ShortFuneral["funeral"]}_${Short["recommend"]}`
  | `${ShortFuneral["funeral"]}_${Short["recommend"]}_list`
  | `${Short["comparison"]}`
  | `${Short["comparison"]}_list`
  | "refund_calc_sangjo"
  | "refund_calc_sangjo_detail"
  | "closed"
  | "crematorium";

export type EventTriggerAndTargetType =
  // 빠르게 알아보기(게스트 견적) 카드: qk-send / qk-done / qk-fail / qk-dup.
  // 이름을 짧게 둔 이유: 최장 조합 click-gyeongsang-funeral_intro-(31자) + suffix가
  // GA4 이벤트명 40자를 넘으면 안 된다. qk_est-* 였을 때 gyeongsang이 42~45자로 초과했다.
  | `qk-${string}`
  // 장례식장 상세 상황별 바로가기 (ParlorIntentLinks). 최장 click-gyeongsang-funeral_intro-it-visit 39자
  | "it-call"
  | "it-mbs"
  | "it-visit"
  // 화장장 시설 상세의 전화 CTA 를 누르면 뜨는 문의 유형 모달.
  // ask-book, ask-etc 는 고이 1666-9784, ask-reloc, ask-visit 는 화장장 직통으로 건다.
  // 이벤트명이 `click-{domain}-{path}-{trigger}` 라 예산이 가장 빠듯한 화면
  // (gyeongsang/nat_fr-crema)이 10자여서 booking, relocation 을 줄였다.
  | "ask-book"
  | "ask-reloc"
  // 장례식장 문의 모달. ask-visit(조문)만 장례식장 직통, 나머지는 고이
  | "ask-binso"
  | "ask-cost"
  | "ask-visit"
  | "ask-etc"
  | "ask-close"
  | "file_request_mdl-submit_success"
  | "quick_mdl_ftr_rght-actn_call"
  | "snackbar"
  | "quick_mdl_ftr_lft-uv_review"
  | "quickcall_mdl-close"
  | "quickcall_mdl-urgent_call-close"
  | "quickcall_mdl-call-close"
  | `quickcall_mdl${string}`
  | `quick_mdl_accordion${string}-opn_dtl`
  | `quick_mdl_ftr${string}-one-actn_call`
  | "cs_center-uv_my"
  | "report"
  | "footer-uv_consultant-search"
  | "file_request_mdl-try_submit"
  | "footer1_checklist_modal"
  | "footer2_discount_chat"
  | "footer3_call"
  | "CTA_uv_mth_req"
  | "top_CTA_mth_req"
  | "CTA_uv_facil_req"
  | "CTA_uv_wreath_req"
  | "CTA_uv_funeral_cost"
  | "A_facil_FAQ_uv_mth_facil_req"
  | "AType_tab"
  | "AType_CTA_uv_prlr_req"
  | "AType_service"
  | "AType_binso_summary"
  | "BType_tab"
  | "CType_tab"
  | "CType_tab-introduce"
  | "CType_tab-price"
  | "CType_tab-binso"
  | "CType_tab-flower"
  | "CType_tab-service"
  | "CType_facil_tab"
  | "DType_facil_tab"
  | "DType_tab"
  | "br_crumb_goi_home"
  | "goi_call"
  | "goi_call_btom"
  | `${ShortFuneral["parlor"]}_goi_call`
  | `${ShortFuneral["crematorium"]}_goi_call`
  | `${ShortFuneral["facility"]}_goi_call`
  | "parlor_goi_call"
  | "facility_goi_call"
  | "crema_goi_call"
  | "emergen-btn_consult-call"
  | "parlor_information"
  | "tab_price"
  | "discount_btn-uv_mtch_rqst_prlr"
  | "uv_wreath_req"
  | "uv_wreath_req-top"
  | "uv_basic_livelihood"
  | "uv_facility"
  | "uv_facility_price"
  | "uv_mth_req"
  | "uv_mth_facil_req"
  | `${ShortFuneral["parlor"]}_uv_mth_req`
  | `${ShortFuneral["crematorium"]}_uv_mth_req`
  | `${ShortFuneral["facility"]}_uv_mth_req`
  | "parlor_uv_mth_req"
  | "facility_uv_mth_req"
  | "crema_uv_mth_req"
  | "uv_mth_req"
  | "uv_mth_req_btom"
  | "uv_prlr_detail"
  | "CTA_expected_cost"
  | "ATypePriceSummary_CTA_prlr_price"
  | "BTypePriceSummary_CTA_prlr_price"
  | "CTypePriceSummary_CTA_prlr_price"
  | "Fixed_CTA"
  | `uv_${ShortFuneral["parlor"]}_${Short["compare"]}_${Short["first"]}_${Short["second"]}`
  | "uv_expert_mth_req"
  | "uv_prlr_item"
  | "uv_crema"
  | "uv_crema_detail"
  | "uv_prlr_req"
  | "uv_prlr_detail"
  | "uv_facility_detail"
  | "uv_funeral_detail"
  | "uv_funeral_place"
  | "uv_funeral_place_price"
  | "uv_first_parlor"
  | "uv_second_parlor"
  | "uv_sigun_prlr_detail"
  | "uv_sido_prlr_detail"
  | "uv_service_detail"
  | "uv_nat_facility_detail"
  | "uv_nat_crema_detail"
  | "uv_wreath_req_btom"
  | `uv_${ShortFuneral["funeral"]}_${Short["process"]}_${Short["recommend"]}`
  | `uv_${ShortFuneral["funeral"]}_${Short["process"]}_${Short["recommend"]}_list`
  | `uv_${ShortFuneral["funeral"]}_${Short["recommend"]}`
  | `uv_${ShortFuneral["funeral"]}_${Short["recommend"]}_list`
  | `uv_${Short["comparison"]}`
  | `uv_${Short["comparison"]}_list`
  | "benef_sup"
  | "benef"
  | "benef_fr_crema-uv_crema"
  | "benef_fr_fac-uv_fac"
  | "br_crumb_nat_hr"
  | "br_crumb_benef"
  | "br_crumb_benef_sup"
  | "br_crumb_nat_merit"
  | "br_crumb_crema"
  | "br_crumb_facility"
  | "mth_req"
  | "br_crumb_mth_req"
  | "br_crumb_mth_facil_req"
  | "br_crumb_sido_prlr_list"
  | "br_crumb_sigungu_prlr_list"
  | "br_crumb_benef_facility"
  | "br_crumb_benef_crema"
  | "br_crumb_veterans"
  | "br_crumb_veterans_facility"
  | "br_crumb_prlr_list"
  | "br_crumb_fr_nat"
  | "br_crumb_fr_benef"
  | "uv_binso"
  | "retry"
  | "sido_prlr_list"
  | "sigungu_prlr_list"
  | "nat_hr_prlr_list"
  | "fr_facility_list"
  | "fr_crema_list"
  | "nat_hr"
  | "nat_merit"
  | "uv_fr_crema_detail"
  | "uv_fr_facility_detail"
  | "consult_btn-call_btom"
  | "all_pro_recm-uv_mth_req"
  | "all_process_recm-call"
  | "all_recm-uv_mth_req_btom"
  | "all_recm-call_btom"
  | "send_question_email"
  | "send_question_email_try"
  | "send_binso_reserve_email_try"
  | "send_binso_reserve_email"
  | "opn_reserve_mdl"
  | "calculate_refund_fee"
  | "uv_guide_cancel_contract"
  | "mdl_CTA"
  | "uv_clsd_detail"
  | "uv_clsd_guide"
  | "mdal_parlor50_CTA"
  | "content-uv_funeral_1"
  | "content-uv_before_4"
  | "item_click";
